<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle :pageTitle="isEditing ? 'Edit Package' : 'Add Package'" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-4">
        <form @submit.prevent="submitForm">
          <div class="row">
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Package Name</label>
              <input type="text" class="form-control" v-model="form.package_name" placeholder="Enter package name" required />
            </div>
            
            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Price (Rp)</label>
              <input type="number" class="form-control" v-model="form.price" placeholder="0" min="0" required />
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Discount (Rp)</label>
              <input type="number" class="form-control" v-model="form.discount" placeholder="0" min="0" />
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Urutan Tampil (Sort Order)</label>
              <input type="number" class="form-control" v-model="form.sort_order" placeholder="0" min="0" />
              <small class="text-muted">Angka lebih kecil tampil lebih awal (default 0).</small>
            </div>
            
            <div class="col-lg-12 col-md-12 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Services Included</label>
              <select class="form-select" multiple v-model="form.service_ids" style="height: 120px;" required>
                <option v-for="service in availableServices" :key="service.service_id" :value="service.service_id">
                  {{ service.service_name }} (Rp {{ Number(service.base_price).toLocaleString('id-ID') }})
                </option>
              </select>
              <small class="text-muted">Hold Ctrl/Cmd to select multiple services</small>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Unit</label>
              <input type="text" class="form-control" v-model="form.unit" placeholder="e.g. per paket, per akun" />
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Material Icon</label>
              <div class="d-flex align-items-center gap-2">
                <select class="form-select" v-model="form.icon">
                  <option value="">-- No Icon --</option>
                  <option value="plagiarism">Plagiarism</option>
                  <option value="edit_document">Edit Document</option>
                  <option value="auto_fix_high">Auto Fix High</option>
                  <option value="menu_book">Menu Book (Journal)</option>
                  <option value="translate">Translate</option>
                  <option value="school">School / Tutorial</option>
                  <option value="info">Info</option>
                  <option value="check_circle">Check Circle</option>
                  <option value="upload_file">Upload File</option>
                  <option value="contact_support">Contact Support</option>
                  <option value="inventory_2">Package / Bundle</option>
                  <option value="description">Description</option>
                  <option value="verified">Verified</option>
                  <option value="star">Star</option>
                </select>
                <span v-if="form.icon" class="material-symbols-outlined fs-24 text-primary">{{ form.icon }}</span>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Navigation Link</label>
              <input type="text" class="form-control" v-model="form.link" placeholder="e.g. /paket-all-in-one" />
            </div>

            <div class="col-lg-12 col-md-12 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Status Akses / Eksklusivitas Kampus Mitra</label>
              <div class="d-flex flex-wrap gap-4 mb-2">
                <div class="form-check">
                  <input class="form-check-input" type="radio" id="pkg_exc_none" value="none" v-model="exclusivityMode">
                  <label class="form-check-label text-dark fs-13" for="pkg_exc_none">
                    <strong>Umum</strong> (Terbuka untuk semua pengguna &amp; publik)
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" id="pkg_exc_all" value="all" v-model="exclusivityMode">
                  <label class="form-check-label text-dark fs-13" for="pkg_exc_all">
                    <strong>Eksklusif Seluruh Kampus Mitra</strong> (Semua mitra terdaftar di RayaCheck)
                  </label>
                </div>
                <div class="form-check">
                  <input class="form-check-input" type="radio" id="pkg_exc_specific" value="specific" v-model="exclusivityMode">
                  <label class="form-check-label text-dark fs-13" for="pkg_exc_specific">
                    <strong>Eksklusif Kampus Tertentu</strong> (Pilih satu atau beberapa kampus)
                  </label>
                </div>
              </div>

              <div v-if="exclusivityMode === 'specific'" class="p-3 border rounded bg-light mt-2">
                <label class="form-label text-dark fs-13 fw-semibold mb-2">Pilih Kampus Mitra yang Memiliki Akses:</label>
                <div class="row g-2">
                  <div v-for="partner in partners" :key="partner.id" class="col-md-4 col-sm-6">
                    <div class="form-check">
                      <input class="form-check-input" type="checkbox" :id="'pkg_partner_' + partner.id" :value="partner.id" v-model="selectedPartnerIds">
                      <label class="form-check-label fs-13" :for="'pkg_partner_' + partner.id">
                        {{ partner.name }}
                      </label>
                    </div>
                  </div>
                </div>
                <div v-if="selectedPartnerIds.length === 0" class="text-danger fs-12 mt-2">
                  * Harap pilih minimal 1 kampus mitra.
                </div>
              </div>
            </div>

            <div class="col-lg-6 col-md-6 mb-4 d-flex align-items-center">
              <div class="form-check form-switch mt-2 me-4">
                <input class="form-check-input" type="checkbox" id="isActive" v-model="form.is_active">
                <label class="form-check-label text-dark fs-14 fw-medium" for="isActive">Active (Visible to users)</label>
              </div>
              <div class="form-check form-switch mt-2">
                <input class="form-check-input" type="checkbox" id="featured" v-model="form.featured">
                <label class="form-check-label text-dark fs-14 fw-medium" for="featured">Featured Package</label>
              </div>
            </div>

            <div class="col-lg-12 col-md-12 mb-4">
              <label class="form-label text-dark fs-14 fw-medium">Description</label>
              <textarea class="form-control" v-model="form.description" rows="3" placeholder="Enter package description"></textarea>
            </div>

            <div class="col-lg-12 col-md-12 mb-4">
              <label class="form-label text-dark fs-14 fw-medium d-flex justify-content-between align-items-center">
                <span>Features / Benefits (Bullet points)</span>
                <button type="button" class="btn btn-sm btn-outline-primary py-1 px-2" @click="addFeature">
                  <i class="ri-add-line me-1"></i>Add Feature
                </button>
              </label>
              <div v-for="(feat, index) in form.features" :key="index" class="d-flex mb-2 align-items-center gap-2">
                <input type="text" class="form-control" v-model="form.features[index]" placeholder="Enter benefit bullet point" required />
                <button type="button" class="btn btn-outline-danger p-2 lh-1" @click="removeFeature(index)" :disabled="form.features.length <= 1">
                  <i class="material-symbols-outlined fs-18">delete</i>
                </button>
              </div>
            </div>

            <!-- Price Tiers -->
            <div class="col-lg-12 col-md-12 mb-4">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0 fs-15 text-dark">Price Tiers (Quantity-based Pricing)</h5>
                <button type="button" class="btn btn-sm btn-outline-primary py-1 px-2" @click="addTier">
                  <i class="ri-add-line me-1"></i>Add Tier
                </button>
              </div>
              <div class="alert alert-info fs-13 py-2 mb-3">
                <i class="ri-information-line me-1"></i>
                Define pricing tiers based on quantity/page count. If no tiers are added, the base price will be used. Leave "Max Qty" empty for open-ended tiers.
              </div>
              
              <div v-for="(tier, index) in form.price_tiers" :key="index" class="p-3 border rounded-3 mb-3 bg-light">
                <div class="d-flex justify-content-between mb-2">
                  <span class="fw-semibold fs-14">Tier #{{ index + 1 }}</span>
                  <button type="button" class="btn btn-sm btn-outline-danger py-0 px-2" @click="removeTier(index)">
                    <i class="ri-delete-bin-line fs-14"></i>
                  </button>
                </div>
                <div class="row g-2">
                  <div class="col-sm-2">
                    <label class="form-label fs-13 mb-1">Min Qty <span class="text-danger">*</span></label>
                    <input type="number" class="form-control form-control-sm" v-model.number="tier.min_qty" required min="1" />
                  </div>
                  <div class="col-sm-2">
                    <label class="form-label fs-13 mb-1">Max Qty</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="tier.max_qty" placeholder="∞" />
                  </div>
                  <div class="col-sm-2">
                    <label class="form-label fs-13 mb-1">Price (Rp) <span class="text-danger">*</span></label>
                    <input type="number" class="form-control form-control-sm" v-model.number="tier.price" required min="0" step="1000" />
                  </div>
                  <div class="col-sm-2">
                    <label class="form-label fs-13 mb-1">Discount (Rp)</label>
                    <input type="number" class="form-control form-control-sm" v-model.number="tier.discount" min="0" step="1000" />
                  </div>
                  <div class="col-sm-2">
                    <label class="form-label fs-13 mb-1">Label</label>
                    <input type="text" class="form-control form-control-sm" v-model="tier.label" placeholder="e.g. 1-10 Halaman" />
                  </div>
                  <div class="col-sm-2">
                    <label class="form-label fs-13 mb-1">Deadline</label>
                    <input type="text" class="form-control form-control-sm" v-model="tier.deadline" placeholder="e.g. 3-5 Hari" />
                  </div>
                  <div class="col-sm-12 mt-2 d-flex align-items-end">
                    <div class="form-check form-switch mb-1">
                      <input class="form-check-input" type="checkbox" :id="'tierActive'+index" v-model="tier.is_active" />
                      <label class="form-check-label fs-12" :for="'tierActive'+index">Active</label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="d-flex justify-content-end gap-2">
            <RouterLink to="/packages" class="btn btn-secondary fs-15 fw-medium">Cancel</RouterLink>
            <button type="submit" class="btn btn-primary fs-15 fw-medium" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ isEditing ? 'Update Package' : 'Create Package' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PageTitle from '@/components/Common/PageTitle.vue';
import packageService from '@/services/packageService';
import serviceService from '@/services/serviceService';
import partnerService from '@/services/partnerService';

export default defineComponent({
  name: 'PackageFormPage',
  components: {
    PageTitle
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    
    const isEditing = ref(false);
    const packageId = ref<number | null>(null);
    const loading = ref(false);
    const availableServices = ref<any[]>([]);
    const partners = ref<any[]>([]);
    const exclusivityMode = ref<'none' | 'all' | 'specific'>('none');
    const selectedPartnerIds = ref<number[]>([]);
    
    const form = ref({
      package_name: '',
      description: '',
      price: 0,
      discount: 0,
      sort_order: 0,
      is_active: true,
      icon: '',
      unit: '',
      featured: false,
      features: [''] as string[],
      link: '',
      service_ids: [] as number[],
      price_tiers: [] as any[]
    });

    const fetchPartners = async () => {
      try {
        const response = await partnerService.getAll();
        partners.value = response.data || [];
      } catch (err) {
        console.error('Error fetching partners:', err);
      }
    };

    onMounted(async () => {
      await fetchPartners();
      await fetchServices();
      if (route.params.id) {
        isEditing.value = true;
        packageId.value = Number(route.params.id);
        await fetchPackage(packageId.value);
      }
    });

    const fetchServices = async () => {
      try {
        const response = await serviceService.getAll();
        availableServices.value = response.data;
      } catch (error) {
        console.error('Error fetching services:', error);
      }
    };

    const fetchPackage = async (id: number) => {
      try {
        const response = await packageService.getById(id);
        const data = response.data;
        let parsedFeatures: string[] = [''];
        if (Array.isArray(data.features)) {
          parsedFeatures = data.features.length > 0 ? [...data.features] : [''];
        } else if (typeof data.features === 'string') {
          try {
            const parsed = JSON.parse(data.features);
            parsedFeatures = Array.isArray(parsed) && parsed.length > 0 ? parsed : (data.features ? [data.features] : ['']);
          } catch (e) {
            parsedFeatures = data.features ? [data.features] : [''];
          }
        }

        if (!data.is_campus_exclusive) {
          exclusivityMode.value = 'none';
          selectedPartnerIds.value = [];
        } else if (data.is_campus_exclusive && (!data.partner_ids || data.partner_ids.length === 0)) {
          exclusivityMode.value = 'all';
          selectedPartnerIds.value = [];
        } else {
          exclusivityMode.value = 'specific';
          selectedPartnerIds.value = Array.isArray(data.partner_ids) ? [...data.partner_ids] : [];
        }

        form.value = {
          package_name: data.package_name,
          description: data.description || '',
          price: data.price,
          discount: data.discount || 0,
          sort_order: Number(data.sort_order ?? 0),
          is_active: !!data.is_active,
          icon: data.icon || '',
          unit: data.unit || '',
          featured: !!data.featured,
          features: parsedFeatures,
          link: data.link || '',
          service_ids: data.services ? data.services.map((s: any) => s.service_id) : [],
          price_tiers: data.price_tiers ? data.price_tiers.map((t: any) => ({
            min_qty: t.min_qty,
            max_qty: t.max_qty,
            price: t.price,
            discount: t.discount || 0,
            label: t.label,
            deadline: t.deadline || '',
            is_active: !!t.is_active
          })) : []
        };
      } catch (error) {
        console.error('Error fetching package:', error);
        router.push('/packages');
      }
    };

    const addFeature = () => {
      form.value.features.push('');
    };

    const removeFeature = (index: number) => {
      if (form.value.features.length > 1) {
        form.value.features.splice(index, 1);
      }
    };

    const addTier = () => {
      form.value.price_tiers.push({
        min_qty: 1,
        max_qty: null,
        price: 0,
        discount: 0,
        label: '',
        deadline: '',
        is_active: true
      });
    };

    const removeTier = (index: number) => {
      form.value.price_tiers.splice(index, 1);
    };

    const submitForm = async () => {
      loading.value = true;
      try {
        let isCampusExclusive = false;
        let partnerIds: number[] | null = null;

        if (exclusivityMode.value === 'all') {
          isCampusExclusive = true;
          partnerIds = null;
        } else if (exclusivityMode.value === 'specific') {
          isCampusExclusive = true;
          partnerIds = selectedPartnerIds.value.length > 0 ? selectedPartnerIds.value : null;
        }

        const payload = {
          ...form.value,
          is_campus_exclusive: isCampusExclusive,
          partner_ids: partnerIds,
          features: form.value.features.filter(f => f.trim() !== ''),
          price_tiers: form.value.price_tiers.map(t => ({
            ...t,
            max_qty: t.max_qty || null
          }))
        };

        if (isEditing.value && packageId.value) {
          await packageService.update(packageId.value, payload);
        } else {
          await packageService.create(payload);
        }
        router.push('/packages');
      } catch (error) {
        console.error('Error saving package:', error);
      } finally {
        loading.value = false;
      }
    };

    return {
      isEditing,
      form,
      loading,
      availableServices,
      addFeature,
      removeFeature,
      addTier,
      removeTier,
      submitForm,
      partners,
      exclusivityMode,
      selectedPartnerIds
    };
  }
});
</script>
